import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { cn } from '@/lib/cn'

type Variant = 'gradient' | 'primary' | 'secondary' | 'ghost'
type Size = 'md' | 'lg'

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-md font-display font-semibold ' +
  'transition-[opacity,background-color,border-color,box-shadow] duration-200 ' +
  'active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 ' +
  'motion-reduce:active:scale-100'

const VARIANTS: Record<Variant, string> = {
  gradient:
    'bg-primary-gradient text-white shadow-[0_0_15px_rgba(93,92,255,0.3)] hover:opacity-90',
  primary: 'bg-primary-container text-on-primary-container hover:opacity-90',
  secondary:
    'glass border border-glass-border bg-surface-slate/40 text-primary backdrop-blur-glass hover:bg-surface-slate/70',
  ghost: 'text-fg-muted hover:bg-white/5 hover:text-fg',
}

const SIZES: Record<Size, string> = {
  md: 'min-h-11 px-5 text-label-sm',
  lg: 'min-h-13 px-8 text-headline-md',
}

interface CommonProps {
  variant?: Variant
  size?: Size
  className?: string
  children: ReactNode
}

type ButtonProps =
  | (CommonProps &
      Omit<ComponentPropsWithoutRef<'button'>, keyof CommonProps> & {
        href?: undefined
      })
  | (CommonProps &
      Omit<ComponentPropsWithoutRef<'a'>, keyof CommonProps> & { href: string })

export function Button({
  variant = 'gradient',
  size = 'md',
  className,
  children,
  ...rest
}: ButtonProps) {
  const classes = cn(BASE, VARIANTS[variant], SIZES[size], className)

  if ('href' in rest && typeof rest.href === 'string') {
    const { href, ...anchorRest } = rest as ComponentPropsWithoutRef<'a'> & {
      href: string
    }
    const internal = href.startsWith('/') && !href.startsWith('//')
    if (internal) {
      return (
        <Link to={href} className={classes} {...anchorRest}>
          {children}
        </Link>
      )
    }
    return (
      <a className={classes} href={href} {...anchorRest}>
        {children}
      </a>
    )
  }

  const buttonProps = rest as ComponentPropsWithoutRef<'button'>
  return (
    <button
      className={classes}
      type={buttonProps.type ?? 'button'}
      {...buttonProps}
    >
      {children}
    </button>
  )
}
